import { Injectable } from '@angular/core';
import JSZip from 'jszip';
import {
	EditS3FileNameRequest,
	S3DeleteFileRequest,
	S3DeleteFileVersionRequest,
	S3DeleteResponse,
	S3File,
	S3FileDownloadResponse,
	S3FileInfoRequest,
	S3FileUploadRequest,
	S3FileVersionUpdateRequest,
	S3GetFileDownloadRequest,
	S3PresignedUrlResponse,
	ConvertToEpsModel
} from '../api-data/ng-openapi-gen-next/models';
import { ApiService } from '../api-data/ng-openapi-gen-next/services/api.service';
import { Observable, filter, lastValueFrom, map } from 'rxjs';
import { StrictHttpResponse } from '../api-data/ng-openapi-gen-next/strict-http-response';
import { RequestBuilder } from '../api-data/ng-openapi-gen-next/request-builder';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { PresignedFileDownloadModel, PresignedUrlFileUploadModel, Result, S3UploadResponseStatus, SuccessResponse } from '../models/models';
import saveAs from 'file-saver';
import { ResultHelper } from '../common/result-extension';
import { ErrorHandlingService } from './error-handling.service';

export const ErrorRetrievingFile: string = 'Error retrieving file';
export const ErrorUploadingFile: string = 'Error uploading file';

@Injectable({
	providedIn: 'root',
})
export class S3FileService {
	/**
	 * Record used to map file extensions such as `pdf`
	 * to their MIME type such as `application/pdf`
	 *
	 * Included file extensions: `pdf` `doc` `rtf` `docx` `xlsx` `xls` `jpg` `jpeg` `bmp` `tiff` `weba` `webm`
	 *
	 * Usage: `FileType['pdf']` evaluates to `'application/pdf'`
	 */
	public FileType: Record<string, string> = {
		pdf: 'application/pdf',
		doc: 'application/msword',
		dot: 'application/msword',
		rtf: 'application/rtf',
		docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

		xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		xls: 'application/vnd.ms-excel',

		ppt: 'application/vnd.ms-powerpoint',
		pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

		msg: 'application/vnd.ms-outlook',
		eml: 'message/rfc822',

		txt: 'text/plain', //

		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		jfif: 'image/jpeg',
		png: 'image/png',
		webp: 'image/webp',
		bmp: 'image/bmp',
		tiff: 'image/tiff',

		mp4: 'video/mp4',
		mov: 'video/quicktime',
		webm: 'video/webm',
		weba: 'audio/webm',

		eps: 'application/postscript',
	};

	constructor(
		private apiV2: ApiService,
		private http: HttpClient,
		private errorHandlingService: ErrorHandlingService
	) {}

	getMimeType(fileType: string): string {
		try {
			const mimeType: string = this.FileType[fileType];
			return mimeType;
		} catch (e) {
			throw new Error(`Don't recognize that type`);
		}
	}

	async uploadS3FilePresignedURL(data: S3PresignedUrlFileUploadRequest): Promise<S3FileUploadResponse> {
		try {
			const arrFileNameParts = data.file.name.split('.');
			const fileType: string = arrFileNameParts[arrFileNameParts.length - 1];
			const s3UploadRequest: S3FileUploadRequest = {
				fileBody: '',
				fileName: data.file.name,
				contentType: this.FileType[fileType],
				fileGUID: data.fileGUID,
				versionGUID: data.versionGUID,
				targetBucketSystemCode: data.targetBucketSystemCode,
				targetPath: data.targetPath,
			};

			const s3UploadPresignedUrlResponse = await lastValueFrom(this.apiV2.fetchS3UploadPresignedUrl({ body: s3UploadRequest }));

			if (s3UploadPresignedUrlResponse.isSuccess && s3UploadPresignedUrlResponse.data?.preSignedUrl) {
				const fileUploadResponse: boolean = await lastValueFrom(this.http.put(s3UploadPresignedUrlResponse.data.preSignedUrl, data.file))
					.then(() => {
						console.log('success Uploading file');
						return true;
					})
					.catch((error: any) => {
						console.error('Error Uploading file', error);
						return false;
					});
				if (fileUploadResponse) {
					return {
						s3FileGUID: s3UploadPresignedUrlResponse.data.s3FileGUID!,
						s3FileVersionGUID: s3UploadPresignedUrlResponse.data.versionGUID,
					};
				} else {
					console.log('failed to upload the file');
					throw new Error('Received presigned url but failed to upload the file');
				}
			} else {
				console.log(s3UploadPresignedUrlResponse);
				throw new Error('Failed to upload file.');
			}
		} catch (e) {
			console.error('Error: ', e);
			throw new Error('Could not upload file.');
		}
	}

	async downloadS3FilePreSignedUrl(request: S3GetFileDownloadRequest, hideToast?: boolean): Promise<PresignedFileDownloadModel | undefined> {
		try {
			const uploadPresignedUrlResponse = await lastValueFrom(this.apiV2.fetchS3ViewPresignedUrl({ body: request }));
			if (uploadPresignedUrlResponse.isSuccess && uploadPresignedUrlResponse.data) {
				let downloadResponse: PresignedFileDownloadModel = {
					fileName: uploadPresignedUrlResponse.data.fileName ?? '',
					presignedUrl: uploadPresignedUrlResponse.data.preSignedUrl ?? '',
					fileType: uploadPresignedUrlResponse.data.fileType ?? '',
					file: undefined,
				};
				if (!hideToast) this.errorHandlingService.showSuccessMessage(`Downloading ${downloadResponse.fileName}`);
				const fetchResponse = await fetch(uploadPresignedUrlResponse.data.preSignedUrl ?? '');
				const blob = await fetchResponse.blob();
				let file: File | undefined;
				if (this.FileType[uploadPresignedUrlResponse.data.fileType ?? '']) {
					file = new File([blob], `${uploadPresignedUrlResponse.data.fileName!}.${uploadPresignedUrlResponse.data.fileType!}`, {
						type: this.FileType[uploadPresignedUrlResponse.data.fileType ?? ''],
					});
				} else {
					file = new File([blob], `${uploadPresignedUrlResponse.data.fileName!}.${uploadPresignedUrlResponse.data.fileType!}`);
				}
				if (!hideToast) this.errorHandlingService.showSuccessMessage(`Finished Download`);
				downloadResponse.file = file;
				return downloadResponse;
			} else {
				console.log('Problem getting presigned url: ', uploadPresignedUrlResponse);
				return;
			}
		} catch (err) {
			console.error('Error: ', err);
			throw 'error';
		}
	}

	async fetchS3FilePreSignedUrl(request: S3GetFileDownloadRequest): Promise<PresignedFileDownloadModel | undefined> {
		try {
			const uploadPresignedUrlResponse = await lastValueFrom(this.apiV2.fetchS3ViewPresignedUrl({ body: request }));
			if (uploadPresignedUrlResponse.isSuccess && uploadPresignedUrlResponse.data) {
				let downloadResponse: PresignedFileDownloadModel = {
					fileName: uploadPresignedUrlResponse.data.fileName ?? '',
					presignedUrl: uploadPresignedUrlResponse.data.preSignedUrl ?? '',
					fileType: uploadPresignedUrlResponse.data.fileType ?? '',
					file: undefined,
				};
				return downloadResponse;
			} else {
				console.log('Problem getting presigned url: ', uploadPresignedUrlResponse);
				return;
			}
		} catch (err) {
			console.error('Error: ', err);
			throw 'error';
		}
	}

	/**
	 * Delete target file
	 *
	 * @param fileGUID target file GUID
	 *
	 * @returns `Promise<boolean>` true or false based on success of deletion
	 */
	async deleteFile(fileID: string): Promise<boolean> {
		return new Promise(async (resolve, reject) => {
			try {
				const deleteRequest: S3DeleteFileRequest = {
					s3FileGUID: fileID,
				};
				const deleteDocumentPromise = await lastValueFrom(this.apiV2.deleteS3File({ body: deleteRequest }));
				if (deleteDocumentPromise.isSuccess && deleteDocumentPromise.data) {
					const deleteDocumentResult: S3DeleteResponse = deleteDocumentPromise.data;
					if (deleteDocumentResult) resolve(true);
				}
				resolve(false);
			} catch (e) {
				console.log('Error: ', e);
				reject(e);
			}
		});
	}

	/**
	 * Gets file info for target file: FileGUID, BucketGUID, FileName, FileType
	 *
	 * @param fileId GUID of target file
	 *
	 * @returns `Promise<S3File>` Promise containing S3File object with file info
	 */
	async getFileInfo(fileId: string): Promise<S3File> {
		return new Promise(async (resolve, reject) => {
			try {
				const fileInfoRequest: S3FileInfoRequest = {
					fileGUID: fileId,
				};
				const fileInfoPromise = await lastValueFrom(this.apiV2.getFileInfo({ body: fileInfoRequest }));
				if (fileInfoPromise.isSuccess && fileInfoPromise.data) {
					resolve(fileInfoPromise.data);
				} else throw Error('File not found');
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * Edit file name
	 *
	 * @param fileId GUID of target file
	 * @param newName String containing new file name
	 *
	 * @returns `Promise<boolean>` Promise containing `true` if the update was successful
	 */
	async editFileName(fileId: string, newName: string): Promise<boolean> {
		return new Promise(async (resolve, reject) => {
			try {
				const editS3FileNameRequest: EditS3FileNameRequest = {
					s3FileGUID: fileId,
					label: newName,
				};
				const editFileNamePromise = await lastValueFrom(this.apiV2.editS3FileName({ body: editS3FileNameRequest }));
				if (editFileNamePromise.isSuccess) {
					resolve(true);
				} else resolve(false);
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * Delete target file
	 *
	 * @param s3FileGUID target file GUID
	 * @param s3FileVersionGUID target file version GUID
	 *
	 * @returns `Promise<boolean>` true or false based on success of deletion
	 */
	async deleteFileVersion(s3FileGUID: string, s3FileVersionGUID: string): Promise<boolean> {
		return new Promise(async (resolve, reject) => {
			try {
				const deleteRequest: S3DeleteFileVersionRequest = {
					s3FileGUID,
					s3FileVersionGUID,
				};
				const deleteDocumentPromise = await lastValueFrom(this.apiV2.deleteS3FileVersion({ body: deleteRequest }));
				if (deleteDocumentPromise.isSuccess && deleteDocumentPromise.data) {
					const deleteDocumentResult: S3DeleteResponse = deleteDocumentPromise.data;
					if (deleteDocumentResult) resolve(true);
				}
				resolve(false);
			} catch (e) {
				console.log('Error: ', e);
				reject(e);
			}
		});
	}

	/**
	 * update file version of target file
	 *
	 * @param s3FileGUID target file GUID
	 * @param s3FileVersionGUID target file version GUID
	 *
	 * @returns `Promise<boolean>` true or false based on success of update
	 */
	async updateS3FileWithVersionGuid(data: S3FileVersionUpdateRequest): Promise<boolean> {
		return new Promise(async (resolve, reject) => {
			try {
				const response = await lastValueFrom(this.apiV2.updateS3FileWithVersionGuid({ body: data }));
				if (response.isSuccess) {
					resolve(true);
				}
				resolve(false);
			} catch (e) {
				console.log('Error: ', e);
				reject(e);
			}
		});
	}

	/**
	 * 
	 */
	async zipFiles(name: string, fileGUIDs: string[], fileDetails: File): Promise<string> {
		return new Promise(async (resolve, reject) => {
			try {
				const zipper: JSZip = new JSZip();
				zipper.file(fileDetails.name, fileDetails);
				for (const fileGUID of fileGUIDs) {
					let response = await this.downloadS3FilePreSignedUrl(
						{
							s3FileGUID: fileGUID ?? '',
							createDownloadLink: true,
						},
						true
					);
					if (response && response.file) zipper.file(response.file.name, response?.file);
				}
				if (Object.keys(zipper.files).length === 0) {
					reject('Failed to zip files');
				}
				await zipper.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 9 } }).then(async (blob) => {
					const url = URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = name;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
				});
				resolve('success');
			} catch (e) {
				reject(e);
			}
		});
	}

	async convertToEps(request: ConvertToEpsModel): Promise<File> {
		return new Promise(async (resolve, reject) => {
			try {
				const s3DownloadResponse = await lastValueFrom(
					this.apiV2.convertToEps({
						body: request,
					})
				);
				if (s3DownloadResponse.isSuccess) {
					const fileData: string = s3DownloadResponse.data?.base64Data!;
					const byteChars = atob(fileData);
					const byteNums = new Array(byteChars.length);
					for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);

					const byteArray = new Uint8Array(byteNums);
					let file: File = new File([byteArray], 'new_qr_code' + '.' + 'eps', { type: this.FileType['eps'] });
					resolve(file);
				} else {
					console.log('No file found to download');
					throw 'No file found to download';
				}
			} catch (e) {
				reject(e);
			}
		});
	}
}

export interface S3PresignedUrlFileUploadRequest {
	file: File;
	fileGUID?: string;
	versionGUID?: string;
	targetBucketSystemCode?: string;
	targetPath?: string;
}

export class S3FileUploadResponse {
	s3FileGUID!: string;
	s3VideoThumbnailGUID?: string;
	s3FileVersionGUID?: string;
}